<template>
  <div class="with-side-panel">
    <div class="content-i">
      <div class="content-box">
        <div class="element-wrapper">

          <BackButton
              :label="$t('general.button.back')"
              :to="{ name: 'meetingRoomList' }"
              marginBottom="5"/>

          <div class="d-flex justify-content-between">
            <h6 class="element-header">
              {{ $t('meeting_room.title') }}
            </h6>
          </div>

          <div class="element-box">
            <form @submit.prevent="submitForm" novalidate>
              <div class="row">
                <div class="col-lg-6 col-md-6 col-sm-12">
                  <BaseInput v-model="formData.name"
                             label="Bezeichnung"
                             :required=true
                             :validation="v$.name"
                             :messages="v$.name.required.$message"/>
                </div>
              </div>
              <div class="row">
                <div class="col-lg-6 col-md-6 col-sm-12">
                  <SelectVue
                      v-model="formData.officeId"
                      :options="officeDropdown"
                      @update:modelValue="updateOfficeColor"
                      :is-clear="true"
                      :required="true"
                      label="Standorte"
                      :placeholder="$t('placeholder.office_name')"
                      :validation="v$.officeId"
                      :messages="v$.officeId.required.$message"
                  />
                </div>
                <div class="col-lg-6 col-md-6 col-sm-12">
                  <div class="color-box mt-4" :style="{ backgroundColor: officeColor }"></div>
                </div>
              </div>
              <div class="row">
                <div class="col-lg-6 col-md-6 col-sm-12">
                  <BaseInput v-model="formData.colorCode"
                             label="Zimmerfarbe"
                             :required=true
                             style="width: 100px; float: left"
                             type="color"
                             :inputStyle="{
                               width: '50px',
                               backgroundColor: '#f0f0f0',
                               padding: '1px',
                             }"/>
                </div>
              </div>
              <div class="form-buttons-w text-center">
                <SaveButton
                    :label="$t('general.button.save')"
                    :loading="isSubmitting"/>
              </div>
            </form>
          </div>
        </div>
      </div>
      <Sidebar></Sidebar>
    </div>
  </div>
</template>

<script setup>
import meetingRoomService from '@/api/MeetingRoom';
import officeService from '@/api/Office';
import BaseInput from '@/components/base/BaseInput.vue';
import CreateButton from '@/components/CreateButton.vue';
import Sidebar from '@/components/FormSidebar.vue';
import SelectVue from '@/components/SelectVue.vue';
import { ErrorToast, SuccessToast } from '@/components/VueToast';
import router from '@/router';
import { getOfficeDropdown } from '@/shared/dropdowns';
import { requiredRule } from '@/shared/validators/validationRules';
import Form from '@/views/App/AddressBook/Client/Form.vue';
import useVuelidate from '@vuelidate/core';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import BackButton from "@/components/button/BackButton.vue";
import SaveButton from "@/components/button/SaveButton.vue";

const { t }          = useI18n();
const officeDropdown = ref([]);
const isSubmitting   = ref(false);
const route          = useRoute();
const meetingRoomId  = route.params.id;
const officeColor    = ref('');

const formData = ref({
  name: '',
  officeId: '',
  colorCode: '#43ad4e',
});

const rules = computed(() => {
  return {
    name: requiredRule(t),
    colorCode: requiredRule(t),
    officeId: requiredRule(t),
  };
});

const v$ = useVuelidate(rules, formData);

async function submitForm() {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  if (isSubmitting.value) return;
  isSubmitting.value = true;

  try {
    if (meetingRoomId) {
      await meetingRoomService.putMeetingRoom(formData.value, +meetingRoomId);
    } else {
      await meetingRoomService.postMeetingRoom(formData.value);
    }

    await router.push({ name: 'meetingRoomList' });
    SuccessToast(t('general.toasts.save_success'));
    isSubmitting.value = false;
  } catch (error) {
    await ErrorToast(error);
  } finally {
    isSubmitting.value = false;
  }
}

async function getMeetingRoom() {
  try {
    const { data }    = await meetingRoomService.getMeetingRoom(+meetingRoomId);
    formData.value    = { ...data.data, officeId: data.data.office.id };
    officeColor.value = data.data.office.colorCode;
  } catch (error) {
    await ErrorToast(error);
  }
}

async function updateOfficeColor(newValue) {
  if (newValue !== undefined && newValue !== null && newValue !== '') {
    const { data }    = await officeService.getOffice(+newValue);
    officeColor.value = data.data.colorCode;
  } else {
    officeColor.value = '';
  }
}

onMounted(async () => {
  await getOfficeDropdown(officeDropdown);

  if (meetingRoomId) {
    await getMeetingRoom();
  }
});

</script>

<style scoped>
.color-box {
  width: 30px;
  height: 30px;
  display: inline-block;
  margin-bottom: 15px;
  border: 1px solid #ccc;
}
</style>